// =============================================================================
// Orb: the theme-aware listening/speaking orb (B-2)
//
// Pure presentation off @careeropt/voice-client's useLivingConversation
// return shape (status/mode/isSpeaking/frequencies) — no SDK access here,
// same separation VoiceDock.tsx (apps/web) established. Adapted from
// VoiceDock's amplitude-driven rAF scale loop, but restyled for Throughline:
// CSS Modules + --tl-* theme vars instead of Tailwind utility classes + the
// `cn`/tailwind-merge helper, so every one of the 3 B-1 themes (warm-paper /
// sage-calm / dusk) tints correctly with ZERO hardcoded colors in this file.
//
// Visual states (mirrors the mockup's orbQuiet/orbListening/orbActive flags,
// plus 'connecting' — see packages/voice-client/src/orbState.ts's header for
// why a live session needs that 4th state the static mockup did not):
//   quiet      — idle disc, the static "M" glyph, no motion (matches the
//                pre-B-2 static disc Refine.tsx shipped in B-1).
//   connecting — same disc, a soft pulse ring (opening the line).
//   listening  — the disc, live-scaled by the input spectrum, cool/support tint.
//   speaking   — the disc, live-scaled by the output spectrum, warm/accent tint.
//
// Respects prefers-reduced-motion (no rAF pulse; a calm static orb instead),
// mirroring VoiceDock.
// =============================================================================

import { deriveOrbVisualState, orbScale } from '@careeropt/voice-client';
import type { LivingFrequencies, LivingMode, LivingStatus } from '@careeropt/voice-client';
import { useEffect, useRef } from 'react';
import clsx from './clsx';
import styles from './Orb.module.css';

export interface OrbProps {
  status: LivingStatus;
  mode: LivingMode;
  isSpeaking: boolean;
  /** Cheap getter for the live visualiser; called inside a rAF loop. Omit for
   *  a static orb (e.g. the pre-session idle render) — the orb then shows
   *  its resting scale with no rAF loop started at all. */
  frequencies?: () => LivingFrequencies;
  /** The glyph shown at the orb's centre (the mockup's "M" for Mira). */
  glyph?: string;
  className?: string;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The live orb. Scale is written directly to the node's transform (not via
 *  React state) so the pulse runs at display refresh rate without
 *  re-rendering the tree every frame — the same technique VoiceDock uses. */
export function Orb({ status, mode, isSpeaking, frequencies, glyph = 'M', className }: OrbProps) {
  const discRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const visual = deriveOrbVisualState({ status, mode, isSpeaking });
  const live = visual === 'listening' || visual === 'speaking';

  useEffect(() => {
    if (!live || !frequencies || prefersReducedMotion()) {
      if (discRef.current) discRef.current.style.transform = 'scale(1)';
      return;
    }
    let mounted = true;
    const tick = () => {
      if (!mounted) return;
      const { input, output } = frequencies();
      const scale = orbScale(input, output);
      if (discRef.current) discRef.current.style.transform = `scale(${scale.toFixed(3)})`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [live, frequencies]);

  return (
    <div
      className={clsx(styles.wrap, className)}
      data-orb-state={visual}
      role="img"
      aria-label={
        visual === 'speaking'
          ? 'Meira speaking'
          : visual === 'listening'
            ? 'Listening'
            : visual === 'connecting'
              ? 'Opening the line'
              : 'Ready to listen'
      }
    >
      {live ? <span className={styles.halo} aria-hidden="true" /> : null}
      {visual === 'connecting' ? <span className={styles.connectingRing} aria-hidden="true" /> : null}
      <div ref={discRef} className={styles.disc} style={{ transformOrigin: 'center' }}>
        <span className={styles.glyph}>{glyph}</span>
      </div>
    </div>
  );
}
